import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

import { NotificationService } from '../../../core/services/notification.service';
import { TechnicianService } from '../../../core/services/technician.service';
import { AuthService } from '../../../core/services/auth.service';
import { Notification } from '../../../core/models/notification.model';
import { Technician } from '../../../core/models/technician.model';

@Component({
  selector: 'app-notification-list',
  imports: [CommonModule],
  templateUrl: './notification-list.html',
  styleUrl: './notification-list.scss',
})
export class NotificationList {
  private readonly notificationService = inject(NotificationService);
  private readonly technicianService = inject(TechnicianService);
  private readonly authService = inject(AuthService);

  protected readonly notifications = signal<Notification[]>([]);
  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);

  protected readonly technicians = signal<Technician[]>([]);
  protected readonly technicianFilter = signal<number | null>(null);
  protected readonly unreadOnly = signal(false);

  protected readonly unreadCount = computed(() => this.notifications().filter((n) => !n.isRead).length);

  constructor() {
    this.technicianService.getTechnicians({ pageSize: 200 }).subscribe({
      next: (r) => {
        this.technicians.set(r.items);

        const signedInEmail = (this.authService.account()?.username ?? '').toLowerCase();
        const own = signedInEmail ? r.items.find((t) => (t.email ?? '').toLowerCase() === signedInEmail) : undefined;
        if (own) {
          this.technicianFilter.set(own.technicianId);
        }
        this.loadNotifications();
      }
    });
  }

  private loadNotifications(): void {
    this.loading.set(true);
    this.error.set(null);
    this.notificationService.getNotifications({
      technicianId: this.technicianFilter() ?? undefined,
      isRead: this.unreadOnly() ? false : undefined,
      pageSize: 100,
    }).subscribe({
      next: (result) => {
        this.notifications.set(result.items);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set('Failed to load notifications. Is the FieldService API running?');
        this.loading.set(false);
        console.error(err);
      }
    });
  }

  protected onTechnicianFilterChange(value: string): void {
    this.technicianFilter.set(value ? Number(value) : null);
    this.loadNotifications();
  }

  protected onUnreadOnlyChange(checked: boolean): void {
    this.unreadOnly.set(checked);
    this.loadNotifications();
  }

  protected markAsRead(notification: Notification): void {
    if (notification.isRead) return;
    this.notificationService.markNotificationRead(notification.notificationId).subscribe({
      next: () => {
        this.notifications.update((list) =>
          list.map((n) => (n.notificationId === notification.notificationId ? { ...n, isRead: true } : n))
        );
      },
      error: (err) => {
        this.error.set('Failed to mark notification as read.');
        console.error(err);
      }
    });
  }

  protected typeColor(type: string | null): string {
    switch (type) {
      case 'JobAssigned': return '#00B9F2';
      case 'JobCancelled': return '#ef4444';
      default: return '#64748B';
    }
  }
}
